<template>
    <div class="is-flex is-justify-content-space-between is-align-items-center is-unselectable">
        <div class="pr-3 is-text-wrap-nowrap">
            {{ getStartTime(matId, scheduledCategory.id) }}
        </div>
        <div class="pr-3 has-text-right">
            <span class="mr-1" v-if="isConflictedScheduledCategory(scheduledCategory.id)">
                <span class="icon has-text-warning">
                    <i class="fas fa-exclamation-triangle"></i>
                </span>
            </span>
            <a v-if="isConflictedScheduledCategory(scheduledCategory.id)" @click="openCategoryConflictsModal">
                {{ scheduledCategory.category }}

                <span v-if="hasSegmentTags" class="ml-1 mr-1">
                    <ScheduleSegmentTag
                        :from="roundMarkerFrom"
                        :to="roundMarkerTo"
                        :assignment-type="scheduledCategory.assignmentType.value"
                    />
                </span>
                <small>({{ scheduledCategory.bracket.totalRealCompetitors }})</small>
            </a>
            <template v-else>
                {{ scheduledCategory.category }}

                <span v-if="hasSegmentTags" class="ml-1 mr-1">
                    <ScheduleSegmentTag
                        :from="roundMarkerFrom"
                        :to="roundMarkerTo"
                        :assignment-type="scheduledCategory.assignmentType.value"
                    />
                </span>
                <small>({{ scheduledCategory.bracket.totalRealCompetitors }})</small>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import type { NamedScheduledCategory, ScheduledCategoryTime } from "../../Api/schedules";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import { eventBus, ON_SHOW_CONFLICTS_FOR_CATEGORY } from "./eventBus";
    import { parseRoundMarker } from "./dropZoneItems";
    import ScheduleSegmentTag from "../ScheduleSegmentTag.vue";

    const props = defineProps<{
        scheduledCategory: NamedScheduledCategory;
        disabled: boolean;
        matId: number;
    }>();

    // Stores
    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    // Computed
    const startTimes = computed(() => showScheduleStore.startTimes);
    const conflictedScheduledCategoryIds = computed(() => showScheduleStore.conflictedScheduledCategoryIds);
    const roundMarkerFrom = computed(() => parseRoundMarker(props.scheduledCategory.roundMarkerFrom));
    const roundMarkerTo = computed(() => parseRoundMarker(props.scheduledCategory.roundMarkerTo));
    const hasSegmentTags = computed(() => {
        return (
            props.scheduledCategory.roundMarkerFrom !== null ||
            props.scheduledCategory.roundMarkerTo !== null ||
            props.scheduledCategory.assignmentType.value > 1
        );
    });

    // Methods
    function getStartTime(matId: number, scheduledCategoryId: number): string {
        const times = startTimes.value;
        if (!times[matId]) {
            return "";
        }
        if (!times[matId][scheduledCategoryId]) {
            return "";
        }
        return formatStartTimeToEventTimezone(times[matId][scheduledCategoryId]);
    }

    function formatStartTimeToEventTimezone(startTime: ScheduledCategoryTime): string {
        const targetTimezone = mainStore.eventTimezone;
        const timezonedBeginningTime = convertToTimezone(startTime.start, targetTimezone, "HH:mm");
        return `${timezonedBeginningTime}`;
    }

    function isConflictedScheduledCategory(scheduledCategoryId: number): boolean {
        return conflictedScheduledCategoryIds.value[scheduledCategoryId] || false;
    }

    function openCategoryConflictsModal() {
        eventBus.$emit(ON_SHOW_CONFLICTS_FOR_CATEGORY, props.scheduledCategory.id);
    }
</script>

<style scoped></style>
